import { IStringLocalizer } from "./IStringLocalizer";

//
// Manages requests to save data via API by queuing them up.
//
// Only one request can be "in flight" at any time.
//
// New additions to the queue replace things on the queue that haven't been processed yet,
// so at any time there are only 3 scenarios:
//
//     A) nothing on the queue. no save requests at all.
//     B) one item on the queue. currently being processed.
//     C) two items on the queue. the first item is being processed, the next item on the queue will be saved when the first finishes
//
// This functions to smooth out save requests and prevents an API hit for every single user input,
// e.g. typing a single character into a text box.
//
// Clients of this class provide an async callback that will hit an api to save data. The callback
// should return true on success, false if there was a failure. The user will be shown an error message
// in case of failure.
//
// This class also manages the "beforeunload" event, and will show a warning to a user
// if they try to leave the page before all data are saved.
//
export default class SaveDataManager<TData> {
    private _isEnabled: boolean;
    private _queue: TData[];
    private _dataObjectSaveHandler: (data: TData) => Promise<boolean>;
    private _localizer: IStringLocalizer;
    private _onNeedsToSave?: () => boolean;

    // ===============================================================================

    /**
     * Instantiates a new object with the given save callback
     * 
     * @param dataObjectSaveHandler the callback to use for saving the data via API
     */
    public constructor(
        dataObjectSaveHandler: (data: TData) => Promise<boolean>,
        localizer: IStringLocalizer
    ) {
        this._isEnabled = false;
        this._queue = [];
        this._dataObjectSaveHandler = dataObjectSaveHandler;
        this._localizer = localizer;
    }

    // ===============================================================================

    /**
     * initializes all events
     */
    public initialize() {
        window.addEventListener("beforeunload", this.window_beforeunload);
    }

    // ===============================================================================

    /**
     * enables saves
     */
    public enable() {
        this._isEnabled = true;
    }

    // ===============================================================================

    /**
     * disables all saves and turns calls to save() into no-ops
     */
    public disable() {
        this._isEnabled = false;
    }

    // ===============================================================================

    /**
     * returns true iff there is more data to save that hasn't been saved yet
     */
    public needsToSave() {
        return this._onNeedsToSave && this._onNeedsToSave(); //(this._queue.length > 0 || (this._onNeedsToSave && this._onNeedsToSave()));
    }

    // ===============================================================================

    public setOnNeedsToSave(onNeedsToSave: () => boolean) {
        this._onNeedsToSave = onNeedsToSave;
    }

    // ===============================================================================

    /**
     * Keeps saving data via the save handler until the queue is empty
     */
    private async initiateSaveProcess() {
        while (this._queue.length > 0) {
            const objectToSave = this._queue[0];

            const success = await this._dataObjectSaveHandler(objectToSave);

            if (!success) {
                alert(this._localizer.localizeString("Sorry, there was a problem saving data."));
                this._queue = [];
                return;
            }

            this._queue = this._queue.slice(1);
        }
    }

    // ===============================================================================

    /**
     * Queues objectToSave to be saved via the save handler. If this object
     * is the first
     * 
     * @param objectToSave the data to save
     */
    public save(objectToSave: TData) {
        if (!this._isEnabled) {
            return;
        }

        if (this._queue.length === 0) {
            // nothing currently being saved -- kick off the save loop

            this._queue = [objectToSave];

            this.initiateSaveProcess();
        }
        else {
            // something already in the process of being saved... just queue this;
            // remove all other items to save except the one currently inflight and this
            // new request
            // the looping save process will later pick this new item up to save

            this._queue = [this._queue[0], objectToSave];
        }
    }

    // This method works the same as the save but it waits for the result to be saved before it exits
    public async saveAsync(objectToSave: TData) {
        if (!this._isEnabled) {
            return;
        }

        if (this._queue.length === 0) {
            // nothing currently being saved -- kick off the save loop

            this._queue = [objectToSave];

            await this.initiateSaveProcess();
        }
        else {
            // something already in the process of being saved... just queue this;
            // remove all other items to save except the one currently inflight and this
            // new request
            // the looping save process will later pick this new item up to save

            this._queue = [this._queue[0], objectToSave];
        }
    }

    // ===============================================================================

    // TODO: nice to have method...
    //public async waitForSaveComplete() {

    //}

    // ===============================================================================

    public alertDataNeedsToBeSaved() {
        alert(this._localizer.localizeString("Please wait for unsaved data to save"));
    }

    // ===============================================================================

    /**
     * Called before the user leaves the page; will tell the browser to show the
     * user a confirmation warning if there is still data left to save
     * 
     * @param e
     */
    private window_beforeunload = (e: BeforeUnloadEvent) => {
        if (this.needsToSave()) {
            // this is the magic incantation needed to tell the browser "show the user a warning confirmation"
            e.preventDefault();

            return e.returnValue = "Please wait for unsaved data to save."; // this string is discarded and a generic message is shown by the browser.
        }
    }
}